// Copyright (c) 2025 Audere, All Rights Reserved

import { useRef, useState, type MouseEvent } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { createStyleSheet } from "../utils/styles";
import type { ProjectData, ProjectListMenuProps } from "../types/projects";
import { formatDate } from "../utils/dates";
import { theme } from "../../theme";

const ProjectListMenu = (props: ProjectListMenuProps) => {
  const {
    startIcon,
    endIconOpen,
    endIconClosed,
    primaryText,
    secondaryText,
    menuItems,
    onMenuItemClick,
    buttonProps,
    isCollapsed,
  } = props;
  const { t } = useTranslation("ProjectListMenu");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasMenu = !!menuItems && menuItems.length > 0;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (hasMenu) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (item: ProjectData) => {
    if (onMenuItemClick) onMenuItemClick(item);
    handleClose();
  };

  return (
    <Box mx={2} sx={{ ...styles.projectListMenuContainer, ...(isCollapsed && { my: 1, mx: 1 }) }}>
      <Tooltip title={isCollapsed && primaryText}>
        <Button
          onClick={handleClick}
          ref={buttonRef}
          data-testid="menu-button"
          {...buttonProps}
          sx={{
            minWidth: isCollapsed ? 40 : "auto",
            border: isCollapsed ? "none" : `1px solid ${theme.palette.grey[300]}`,
            py: isCollapsed ? 0 : 2,
          }}
        >
          {isCollapsed ? (
            <Box>
              {startIcon}
              {anchorEl ? endIconOpen : endIconClosed}
            </Box>
          ) : (
            <>
              {startIcon}
              <Box sx={styles.buttonTextContainer}>
                <Typography variant="body2" color="textPrimary" sx={styles.removeTransform}>
                  {primaryText}
                </Typography>
                <Typography variant="body1bold" color="textPrimary" sx={styles.removeTransform}>
                  {secondaryText}
                </Typography>
              </Box>
              {anchorEl ? endIconOpen : endIconClosed}
            </>
          )}
        </Button>
      </Tooltip>
      {hasMenu && (
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                ...styles.menuPaper,
                width: buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "auto",
              },
            },
          }}
        >
          {menuItems!.map((item: ProjectData, idx: number) => (
            <Box key={idx}>
              <MenuItem onClick={() => handleMenuItemClick(item)}>
                <Box sx={styles.menuTextContainer}>
                  <Typography variant="body2" color="textSecondary" sx={styles.removeTransform}>
                    {formatDate(item.last_modified?.split("T")[0])}
                  </Typography>
                  <Typography variant="body1bold" color="primary" sx={styles.removeTransform}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={styles.removeTransform}>
                    {t("test", { count: item.number_rdts })}
                  </Typography>
                </Box>
              </MenuItem>
              {idx < menuItems.length - 1 && <Divider />}
            </Box>
          ))}
        </Menu>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  projectListMenuContainer: {
    width: "80%",
  },
  buttonTextContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    flex: 1,
    mx: 1,
  },
  removeTransform: {
    textTransform: "none",
  },
  menuTextContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    gap: 1,
  },
  menuPaper: {
    minWidth: theme.spacing(35),
    marginTop: 2,
  },
});

export default ProjectListMenu;
